import { useEffect, useMemo, useRef } from 'react';

import type { Options } from 'highcharts';

import { BiomassDistributionBin, MeasurementStatisticalAnalysis } from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';

import { AutoResizeHighChart } from '@/components/chart/AutoResizeHighChart.tsx';
import {
  bgColor,
  defaultChartColors,
  defaultChartOptions,
  defaultTooltipOptions,
  defaultXAxisOptions,
  defaultYAxisOptions,
  getChartOptionsWithLocale,
  getLocaleFromLanguage,
  histogramColor,
  lineWidth,
  updateChartLocale,
} from '@/components/chart/chart/defaultHighChartOptions.ts';
import Highcharts from '@/components/chart/highcharts';
import { useBiomassDistributionPreferencesStorage } from '@/stores';

import type { BiomassDistributionBinSize } from './biomassDistribution.types.ts';
import {
  formatBinInterval,
  formatDistributionStatistic,
  getBinCenter,
  getBinSizeValue,
  getIntervalLabelWidth,
  getIntervalTickPositions,
  getPercentageString,
  getPlottedSkewness,
  getSkewnessLean,
  getSkewNormalCurvePoints,
  isPlottedBin,
} from './biomassDistribution.utils.ts';

/** Kept distinct from the histogram and from either p-value colour of the fitted curve. */
const normalCurveColor = defaultChartColors[2];
const ESTIMATED_DISTRIBUTION_SERIES_ID = 'estimatedDistribution';
const NORMAL_DISTRIBUTION_SERIES_ID = 'normalDistribution';

/** Lazy factory — must not spread `defaultChartOptions` at module init (chart barrel import order). */
const getBiomassDistributionBaseOptions = (): Options => ({
  ...defaultChartOptions,
  chart: {
    ...defaultChartOptions.chart,
    type: 'column',
    inverted: false,
    spacingBottom: 10,
    // The density axis has its own scale. Aligning its ticks with the percentage axis inserts
    // extra horizontal lines that do not sit on the percentage grid.
    alignTicks: false,
  },
  legend: {
    enabled: true,
    ...defaultChartOptions.legend,
  },
  xAxis: {
    ...defaultXAxisOptions,
    type: 'linear',
  },
  yAxis: [
    {
      ...defaultYAxisOptions,
      // Percentage grid only. Minor lines are extra horizontals on this axis.
      minorGridLineWidth: 0,
    },
    {
      ...defaultYAxisOptions,
      opposite: true,
      // Scales the curves. Its own grid is a second set of horizontals, near the top while the
      // curves are shown, and is not part of the percentage scale.
      gridLineWidth: 0,
      minorGridLineWidth: 0,
      lineWidth: 0,
      tickWidth: 0,
      labels: { enabled: false },
      title: { text: undefined },
    },
  ],
  tooltip: {
    formatter: undefined,
  },
});

interface BiomassDistributionChartOptions {
  widgetId: string;
  /** Contiguous bins from the distribution endpoint, bounds in kg. */
  bins: BiomassDistributionBin[];
  totalCount: number;
  statistics: MeasurementStatisticalAnalysis | undefined;
  binSize: BiomassDistributionBinSize;
}

export const BiomassDistributionChart = ({
  widgetId,
  bins,
  totalCount,
  statistics,
  binSize,
}: BiomassDistributionChartOptions) => {
  const {
    t,
    i18n: { language },
  } = useTranslation(['orbitOne', 'chartConfig']);
  const highChartsRef = useRef<HighchartsReact.RefObject>(null);
  const {
    estimatedCurveVisible,
    normalCurveVisible,
    setEstimatedCurveVisible,
    setNormalCurveVisible,
  } = useBiomassDistributionPreferencesStorage(widgetId);

  // Keep visibility out of the effect deps so a legend toggle does not wipe and rebuild series.
  const curveVisibilityRef = useRef({ estimatedCurveVisible, normalCurveVisible });
  curveVisibilityRef.current = { estimatedCurveVisible, normalCurveVisible };

  useEffect(() => {
    const pValue = statistics?.shapiroWilkTestResult?.pValue;
    const pColor = pValue != null && pValue >= 0.05 ? defaultChartColors[1] : defaultChartColors[7];

    const binSizeValue = getBinSizeValue(binSize);

    const plottedBins = bins
      .filter(({ count }) => isPlottedBin(count, totalCount))
      .map(({ lowerBound, count }) => ({
        binCenter: getBinCenter(lowerBound, binSizeValue),
        count,
      }));

    const dataBins = plottedBins.map(({ binCenter, count }) => [binCenter, count]);
    const binCenters = plottedBins.map(({ binCenter }) => binCenter);
    const intervalLabelWidth = getIntervalLabelWidth(
      binCenters.map(binCenter => formatBinInterval(binCenter, binSize, language))
    );

    const chart = highChartsRef.current?.chart;

    while (chart?.series.length) chart.series[0]?.remove();

    const locale = getLocaleFromLanguage(language);

    Highcharts.setOptions({
      lang: {
        locale: locale,
      },
    });

    updateChartLocale(chart, locale);

    chart?.xAxis[0]?.update({
      ...defaultXAxisOptions,
      type: 'linear',
      title: {
        ...defaultXAxisOptions.title,
        text: t('orbitOne:biomassDistribution.labels.biomass'),
      },
      tickPositioner: function (min, max) {
        return getIntervalTickPositions({
          binCenters,
          min,
          max,
          axisLength: this.len,
          labelWidth: intervalLabelWidth,
        });
      },
      labels: {
        formatter: function () {
          return formatBinInterval(this.value as number, binSize, language);
        },
      },
    });

    chart?.yAxis[0]?.update({
      title: {
        ...defaultYAxisOptions.title,
        text: undefined,
      },
      labels: {
        formatter: function () {
          return getPercentageString(totalCount, this.value as number, language);
        },
      },
      minorGridLineWidth: 0,
      plotLines: [],
    });

    chart?.addSeries({
      name: t('orbitOne:biomassDistribution.histogramLabel', { totalObservationCount: totalCount }),
      type: 'column',
      data: dataBins,
      showInLegend: true,
      color: histogramColor,
      borderColor: bgColor,
      groupPadding: 0,
      pointPadding: 0,
      pointRange: binSizeValue,
      tooltip: {
        ...defaultTooltipOptions,
        headerFormat: undefined,
        pointFormatter: function () {
          return `${formatBinInterval(this.x, binSize, language)} kg
                  <br>${t('orbitOne:biomassDistribution.measurementFraction')}: <strong>${getPercentageString(totalCount, this.y, language)}</strong>
                  <br>${t('orbitOne:biomassDistribution.numberOfObservations')}: <strong>${this.y}</strong>`;
        },
      },
    });

    const curvePoints =
      statistics && totalCount > 2
        ? getSkewNormalCurvePoints(
            statistics.mean,
            statistics.standardDeviation,
            statistics.skewness
          )
        : [];

    if (curvePoints.length > 0 && statistics) {
      const { mean, standardDeviation, skewness } = statistics;
      const plottedSkewness = getPlottedSkewness(skewness);
      const { estimatedCurveVisible: estimatedVisible, normalCurveVisible: normalVisible } =
        curveVisibilityRef.current;

      // Both curves share these two statistics; only the fitted one also uses the skewness.
      const meanAndDeviationRows = `${t('chartConfig:linear.mean')}: <strong>${formatDistributionStatistic(mean, language)}</strong> kg
                    <br>${t('chartConfig:linear.standardDeviation')}: <strong>${formatDistributionStatistic(standardDeviation, language)}</strong> kg`;

      chart?.addSeries({
        id: ESTIMATED_DISTRIBUTION_SERIES_ID,
        name: t('orbitOne:biomassDistribution.bellCurveLabel'),
        type: 'areaspline',
        data: curvePoints,
        opacity: 0.5,
        yAxis: 1,
        // Default hidden — the histogram is the primary view. Visibility is restored from the
        // per-widget session preference so legend toggles survive data refreshes and chart/table
        // switches.
        visible: estimatedVisible,
        showInLegend: true,
        color: pColor,
        fillOpacity: 0.25,
        marker: { enabled: false },
        events: {
          show: () => setEstimatedCurveVisible(true),
          hide: () => setEstimatedCurveVisible(false),
        },
        // The curve is fitted from these three statistics, so the tooltip explains it with them
        // rather than with the hovered sample's density, which carries no meaning on its own.
        tooltip: {
          ...defaultTooltipOptions,
          headerFormat: undefined,
          pointFormatter: function () {
            return `${this.series.name}
                    <br>${meanAndDeviationRows}
                    <br>${t('orbitOne:biomassDistribution.bellCurveTooltip.skewness')}: <strong>${formatDistributionStatistic(plottedSkewness, language)}</strong>
                    <br>${t(`orbitOne:biomassDistribution.bellCurveTooltip.skewnessLean.${getSkewnessLean(plottedSkewness)}`)}`;
          },
        },
      });

      // Same mean and standard deviation, skewness left out, as a dashed baseline to compare the
      // fitted curve against.
      chart?.addSeries({
        id: NORMAL_DISTRIBUTION_SERIES_ID,
        name: t('orbitOne:biomassDistribution.normalCurveLabel'),
        type: 'spline',
        data: getSkewNormalCurvePoints(mean, standardDeviation),
        yAxis: 1,
        showInLegend: true,
        visible: normalVisible,
        color: normalCurveColor,
        dashStyle: 'Dash',
        lineWidth,
        marker: { enabled: false },
        events: {
          show: () => setNormalCurveVisible(true),
          hide: () => setNormalCurveVisible(false),
        },
        tooltip: {
          ...defaultTooltipOptions,
          headerFormat: undefined,
          pointFormatter: function () {
            return `${this.series.name}
                    <br>${meanAndDeviationRows}`;
          },
        },
      });
    }

    chart?.redraw();
  }, [
    language,
    t,
    binSize,
    bins,
    totalCount,
    statistics,
    setEstimatedCurveVisible,
    setNormalCurveVisible,
  ]);

  const chartOptionsWithLocale = useMemo(
    () => getChartOptionsWithLocale(getBiomassDistributionBaseOptions(), language),
    [language]
  );

  return (
    <AutoResizeHighChart
      highcharts={Highcharts}
      options={chartOptionsWithLocale}
      highChartsRef={highChartsRef}
    />
  );
};
